import React, { useState, useEffect, useCallback } from 'react';
import EmployeeList from '../components/EmployeeList';
import EmployeeForm from '../components/EmployeeForm';
import BranchSelector from '../components/BranchSelector';
import { branchAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import { Card } from '../components/SharedComponents';
import { FiUsers } from '../components/Icons';

// Display order: leadership first, then each craft with its supervisor ahead.
const ROLE_ORDER = {
  admin: 0,
  store_manager: 1,
  manager_assistant: 2,
  makeup_supervisor: 3,
  makeup: 4,
  photographer_supervisor: 5,
  photographer: 6,
  retoucher_supervisor: 7,
  retoucher: 8,
};

const PageContainer = styled.div`
  width: 100%;
  padding: ${(p) => p.theme.spacing.lg};
  box-sizing: border-box;
  max-width: none;
`;

const HeaderCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;
  margin-bottom: ${(p) => p.theme.spacing.xl};

  h1 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${(p) => p.theme.spacing.sm};
  }

  p {
    margin: ${(p) => p.theme.spacing.xs} 0 0 0;
    color: ${(p) => p.theme.colors.text.secondary};
    font-size: 0.95rem;
  }
`;

const BranchField = styled.div`
  min-width: 280px;

  label {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.text.secondary};
    margin-bottom: 6px;
  }

  @media (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    min-width: 100%;
  }
`;

const EmptyState = styled(Card)`
  text-align: center;
  padding: ${(p) => p.theme.spacing.xxl};

  h3 {
    margin-bottom: ${(p) => p.theme.spacing.sm};
  }

  p {
    color: ${(p) => p.theme.colors.text.secondary};
    margin: 0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(43, 40, 37, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: ${(p) => p.theme.spacing.md};
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border.light};
  border-radius: ${(p) => p.theme.borderRadius.large};
  box-shadow: ${(p) => p.theme.shadows.heavy};
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: ${(p) => p.theme.spacing.xl};
  animation: slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes slideUp {
    from { transform: translateY(16px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @media (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    padding: ${(p) => p.theme.spacing.lg};
  }
`;

const EmployeesPage = () => {
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchStaff, setBranchStaff] = useState([]);
  const [loading, setLoading] = useState(false);

  const { accessibleBranches, homeBranchId } = useAuth();

  useEffect(() => {
    // Auto-select home branch first, then fall back to first accessible branch
    if (!selectedBranch) {
      if (homeBranchId && (accessibleBranches === 'ALL_BRANCHES' || accessibleBranches.includes(homeBranchId))) {
        setSelectedBranch(homeBranchId);
      } else if (Array.isArray(accessibleBranches) && accessibleBranches.length > 0) {
        setSelectedBranch(accessibleBranches[0]);
      }
    }
  }, [accessibleBranches, homeBranchId, selectedBranch]);

  const fetchBranchStaff = useCallback(async () => {
    if (!selectedBranch) return;

    setLoading(true);
    try {
      const response = await branchAPI.getStaff(selectedBranch);
      const sortedStaff = [...response.data].sort((a, b) => {
        const roleA = ROLE_ORDER[a.role] ?? 999;
        const roleB = ROLE_ORDER[b.role] ?? 999;
        if (roleA === roleB) return a.name.localeCompare(b.name);
        return roleA - roleB;
      });
      setBranchStaff(sortedStaff);
    } catch (error) {
      console.error('Error fetching branch staff:', error);
      setBranchStaff([]);
    } finally {
      setLoading(false);
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchBranchStaff();
  }, [fetchBranchStaff]);

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserForm(true);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setShowUserForm(true);
  };

  const handleCloseUserForm = () => {
    setShowUserForm(false);
    setEditingUser(null);
    fetchBranchStaff();
  };

  return (
    <PageContainer>
      <HeaderCard>
        <div>
          <h1><FiUsers size={24} /> Users Management</h1>
          <p>Manage the people of each branch — profiles, roles, holiday groups and status.</p>
        </div>
        <BranchField>
          <label>Branch</label>
          <BranchSelector
            value={selectedBranch}
            onChange={setSelectedBranch}
            placeholder="Select branch to view staff"
          />
        </BranchField>
      </HeaderCard>

      {selectedBranch ? (
        <EmployeeList
          employees={branchStaff}
          loading={loading}
          onEdit={handleEditUser}
          onAdd={handleAddUser}
          onRefresh={fetchBranchStaff}
        />
      ) : (
        <EmptyState>
          <h3>Select a branch</h3>
          <p>Choose a branch above to view and manage its employees.</p>
        </EmptyState>
      )}

      {showUserForm && (
        <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget) handleCloseUserForm(); }}>
          <ModalContent>
            <EmployeeForm
              user={editingUser}
              defaultBranch={selectedBranch}
              onCancel={handleCloseUserForm}
              onSave={handleCloseUserForm}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default EmployeesPage;
